/**
 * LoRa Network Intelligence — salud de la red de telemetría como activo
 * de negocio (no "best effort").
 *
 * Cubre lo que el caso de negocio exige: PDR por gateway/dispositivo,
 * ADR effectiveness, SF sugerido vs vigente, diversidad de gateways,
 * autonomía de batería y recomendaciones accionables (reubicar gateway,
 * ajustar SF, reemplazar dispositivo).  Traduce señal RF en m³ de
 * lectura recuperable y menos OPEX de terreno.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { api } from "../api/client";
import type { LoraGateway, LoraDevice, LoraRecommendation } from "../api/mock";
import { Icon } from "../components/Icon";
import { PageHeader } from "../layout/PageHeader";
import { useToast } from "../system/ToastContext";
import { exportRowsToCsv } from "../lib/exporters";

interface LoraSnapshot {
  kpis: {
    pdr_red_pct: number;
    lecturas_completas_pct: number;
    adr_effectiveness_pct: number;
    dispositivos_total: number;
    dispositivos_criticos: number;
    bateria_critica: number;
    gateways_online: number;
    gateways_total: number;
    uplinks_h: number;
  };
  gateways: LoraGateway[];
  devices: LoraDevice[];
  recomendaciones: LoraRecommendation[];
  pdr_trend: number[];
  _source?: string;
}

function tone(v: number, ok: number, warn: number): "accent" | "warn" | "danger" {
  if (v >= ok) return "accent";
  if (v >= warn) return "warn";
  return "danger";
}

export function LoraIntelligence(): JSX.Element {
  const { toast } = useToast();
  const [tab, setTab] = useState<"gateways" | "dispositivos">("gateways");
  const q = useQuery<LoraSnapshot>({
    queryKey: ["lora"],
    queryFn: () => api<LoraSnapshot>("/lora"),
    staleTime: 30_000,
  });
  const s = q.data;

  const pdrSeries = useMemo(
    () => (s?.pdr_trend ?? []).map((v, i) => ({ idx: `S-${6 - i}`, pdr: v })),
    [s],
  );

  // Dispositivos con SF subóptimo (oportunidad de optimización).
  const sfMismatch = (s?.devices ?? []).filter((d) => d.sf_actual !== d.sf_sugerido);

  return (
    <>
      <PageHeader
        eyebrow="GIS & Red"
        title="LoRa Network Intelligence"
        subtitle="La red de telemetría como activo de negocio: PDR, ADR, spreading factor, batería y diversidad de gateways."
        actions={
          <>
            {s?._source === "rf-api" && (
              <span className="badge badge--accent" title="Datos en vivo del rf-api (DuckDB)">
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "var(--c-success)",
                    display: "inline-block",
                    marginRight: 5,
                  }}
                />
                EN VIVO · rf-api
              </span>
            )}
            <button
              className="btn"
              onClick={() => {
                const ok = exportRowsToCsv(
                  `lora-dispositivos-${new Date().toISOString().slice(0, 10)}.csv`,
                  (s?.devices ?? []).map((d) => ({
                    device_id: d.device_id,
                    dma: d.dma_codigo,
                    pdr_pct: d.pdr_pct,
                    rssi_dbm: d.rssi_dbm,
                    snr_db: d.snr_db,
                    sf_actual: d.sf_actual,
                    sf_sugerido: d.sf_sugerido,
                    adr: d.adr_activo ? "on" : "off",
                    bateria_pct: d.bateria_pct,
                    gateways_visibles: d.gateways_visibles,
                    riesgo: d.riesgo,
                  })),
                );
                toast(ok ? "Inventario LoRa exportado" : "Sin datos", ok ? "success" : "warn");
              }}
            >
              <Icon name="report" size={13} /> Exportar inventario
            </button>
          </>
        }
      />

      {s && (
        <>
          <div className="card-grid" style={{ marginBottom: 16 }}>
            <div className={`kpi kpi--${tone(s.kpis.pdr_red_pct, 95, 85)}`}>
              <span className="kpi__label">PDR red</span>
              <span className="kpi__value">{s.kpis.pdr_red_pct} %</span>
              <span className="kpi__hint">packet delivery ratio</span>
            </div>
            <div className={`kpi kpi--${tone(s.kpis.lecturas_completas_pct, 95, 85)}`}>
              <span className="kpi__label">Lecturas completas</span>
              <span className="kpi__value">{s.kpis.lecturas_completas_pct} %</span>
              <span className="kpi__hint">% lecturas diarias recibidas</span>
            </div>
            <div className="kpi kpi--info">
              <span className="kpi__label">ADR effectiveness</span>
              <span className="kpi__value">{s.kpis.adr_effectiveness_pct} %</span>
              <span className="kpi__hint">dispositivos con ADR activo</span>
            </div>
            <div className="kpi kpi--magenta">
              <span className="kpi__label">Uplinks / hora</span>
              <span className="kpi__value">{(s.kpis.uplinks_h / 1000).toFixed(1)}k</span>
              <span className="kpi__hint">
                {s.kpis.gateways_online}/{s.kpis.gateways_total} gateways online
              </span>
            </div>
            <div className="kpi kpi--danger">
              <span className="kpi__label">Batería crítica</span>
              <span className="kpi__value">{s.kpis.bateria_critica}</span>
              <span className="kpi__hint">dispositivos &lt; 15%</span>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginBottom: 16 }}>
            <section className="card">
              <div className="card__title">
                <span>Tendencia PDR de red (6 semanas)</span>
                <span className="text-mono text-muted">objetivo ≥ 95%</span>
              </div>
              <div style={{ width: "100%", height: 200 }}>
                <ResponsiveContainer>
                  <AreaChart data={pdrSeries} margin={{ top: 6, right: 8, bottom: 0, left: -6 }}>
                    <defs>
                      <linearGradient id="grad-pdr" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--c-primary-300)" stopOpacity={0.5} />
                        <stop offset="95%" stopColor="var(--c-primary-300)" stopOpacity={0.04} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                    <XAxis dataKey="idx" stroke="var(--chart-axis)" tick={{ fontSize: 11 }} />
                    <YAxis stroke="var(--chart-axis)" tick={{ fontSize: 11 }} domain={[90, 100]} unit="%" />
                    <Tooltip
                      contentStyle={{
                        background: "var(--c-surface)",
                        border: "1px solid var(--c-border)",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={(v: number) => [`${v} %`, "PDR"]}
                    />
                    <Area type="monotone" dataKey="pdr" stroke="var(--c-primary-300)" strokeWidth={2} fill="url(#grad-pdr)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="card">
              <div className="card__title">Optimización disponible</div>
              <div className="alert-component">
                <span>SF subóptimo</span>
                <strong>{sfMismatch.length} dispositivos</strong>
              </div>
              <div className="alert-component">
                <span>Sin diversidad de GW</span>
                <strong>
                  {s.devices.filter((d) => d.gateways_visibles < 2).length} dispositivos
                </strong>
              </div>
              <div className="alert-component">
                <span>Retries altos (&gt;15%)</span>
                <strong>{s.devices.filter((d) => d.retries_pct > 15).length} dispositivos</strong>
              </div>
              <button
                className="btn btn--primary"
                style={{ marginTop: 10, width: "100%" }}
                onClick={() =>
                  toast(
                    `Plan ADR/SF aplicado a ${sfMismatch.length} dispositivos (simulación)`,
                    "success",
                  )
                }
              >
                <Icon name="settings" size={13} /> Aplicar plan de optimización
              </button>
            </section>
          </div>

          {/* Recomendaciones accionables */}
          <section className="card" style={{ marginBottom: 16 }}>
            <div className="card__title">Recomendaciones de red</div>
            <div className="vstack" style={{ gap: 8 }}>
              {s.recomendaciones.map((rec) => (
                <div
                  key={rec.rec_id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto",
                    gap: 12,
                    alignItems: "center",
                    padding: "10px 12px",
                    border: "1px solid var(--c-border)",
                    borderRadius: 8,
                    borderLeft: `3px solid ${
                      rec.prioridad === "alta" ? "var(--c-danger)" : rec.prioridad === "media" ? "var(--c-warn)" : "var(--c-info)"
                    }`,
                  }}
                >
                  <span
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 7,
                      display: "grid",
                      placeItems: "center",
                      background: "var(--c-surface-3)",
                      color: "var(--c-primary-300)",
                    }}
                  >
                    <Icon name="wifi" size={14} />
                  </span>
                  <div>
                    <div style={{ fontSize: 13, color: "var(--c-text-strong)", fontWeight: 600 }}>
                      {rec.titulo}
                    </div>
                    <div className="text-muted" style={{ fontSize: 12 }}>{rec.detalle}</div>
                    <div style={{ marginTop: 4 }}>
                      <span className="badge badge--accent">{rec.impacto}</span>
                    </div>
                  </div>
                  <Link to={rec.to} className="btn btn--ghost">
                    Abrir <Icon name="chevron-right" size={11} />
                  </Link>
                </div>
              ))}
            </div>
          </section>

          {/* Tabs gateways / dispositivos */}
          <div className="hstack" style={{ marginBottom: 8 }}>
            <div className="segmented">
              <button
                className="segmented__btn"
                aria-pressed={tab === "gateways"}
                onClick={() => setTab("gateways")}
              >
                Gateways
              </button>
              <button
                className="segmented__btn"
                aria-pressed={tab === "dispositivos"}
                onClick={() => setTab("dispositivos")}
              >
                Dispositivos
              </button>
            </div>
          </div>

          {tab === "gateways" ? (
            <section className="card">
              <div className="table-wrapper" style={{ borderColor: "transparent" }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Gateway</th>
                      <th>DMA</th>
                      <th>Estado</th>
                      <th className="numeric">PDR</th>
                      <th className="numeric">RSSI</th>
                      <th className="numeric">SNR</th>
                      <th className="numeric">Saturación</th>
                      <th className="numeric">Uplinks/h</th>
                      <th className="numeric">Medidores</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.gateways.map((g) => (
                      <tr key={g.gateway_id}>
                        <td className="text-mono">{g.gateway_id}</td>
                        <td>{g.dma_codigo}</td>
                        <td>
                          <span className={`badge badge--${g.estado === "online" ? "accent" : g.estado === "degradado" ? "warn" : "danger"}`}>
                            {g.estado}
                          </span>
                        </td>
                        <td className="numeric">
                          <span className={`badge badge--${tone(g.pdr_pct, 95, 80)}`}>{g.pdr_pct}%</span>
                        </td>
                        <td className="numeric">{g.rssi_dbm} dBm</td>
                        <td className="numeric">{g.snr_db} dB</td>
                        <td className="numeric">
                          <span className={g.saturacion_pct > 60 ? "text-strong" : ""}>{g.saturacion_pct}%</span>
                        </td>
                        <td className="numeric">{g.uplinks_h.toLocaleString("es-CL")}</td>
                        <td className="numeric">{g.medidores.toLocaleString("es-CL")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : (
            <section className="card">
              <div className="table-wrapper" style={{ borderColor: "transparent" }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Dispositivo</th>
                      <th>DMA</th>
                      <th className="numeric">PDR</th>
                      <th className="numeric">SF</th>
                      <th>ADR</th>
                      <th className="numeric">Batería</th>
                      <th className="numeric">GW vis.</th>
                      <th>Riesgo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.devices.map((d) => (
                      <tr key={d.device_id}>
                        <td className="text-mono">{d.device_id}</td>
                        <td>{d.dma_codigo}</td>
                        <td className="numeric">
                          <span className={`badge badge--${tone(d.pdr_pct, 90, 75)}`}>{d.pdr_pct}%</span>
                        </td>
                        <td className="numeric">
                          {d.sf_actual !== d.sf_sugerido ? (
                            <span title={`Sugerido SF${d.sf_sugerido}`}>
                              <span className="text-strong">SF{d.sf_actual}</span>
                              <span className="text-muted"> → SF{d.sf_sugerido}</span>
                            </span>
                          ) : (
                            <span>SF{d.sf_actual}</span>
                          )}
                        </td>
                        <td>
                          <span className={`badge badge--${d.adr_activo ? "accent" : "neutral"}`}>
                            {d.adr_activo ? "on" : "off"}
                          </span>
                        </td>
                        <td className="numeric">
                          <span className={`badge badge--${tone(d.bateria_pct, 30, 15)}`}>
                            {d.bateria_pct}% · {d.bateria_meses_rest}m
                          </span>
                        </td>
                        <td className="numeric">
                          <span className={d.gateways_visibles < 2 ? "text-strong" : ""}>
                            {d.gateways_visibles}
                          </span>
                        </td>
                        <td>
                          <span className={`badge badge--${d.riesgo === "ok" ? "accent" : d.riesgo === "vigilancia" ? "warn" : "danger"}`}>
                            {d.riesgo}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
}
